import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const EmailManagement = () => {
  const [loading, setLoading] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [emailType, setEmailType] = useState('individual');
  const [formData, setFormData] = useState({
    recipientEmail: '',
    subject: '',
    messageBody: '',
    title: '',
    message: '',
  });
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchEmailStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/email/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmailStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  useEffect(() => { fetchEmailStats(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailType === 'bulk' && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      let endpoint, body;

      if (emailType === 'individual') {
        endpoint = '/api/email/individual';
        body = {
          recipientEmail: formData.recipientEmail,
          subject: formData.subject,
          message: formData.messageBody
        };
      } else {
        endpoint = '/api/email/bulk';
        body = {
          title: formData.title,
          message: formData.message,
          sendToAll: true
        };
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      setResult(data);

      if (data.success) {
        setFormData({
          recipientEmail: '',
          subject: '',
          messageBody: '',
          title: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setResult({ success: false, message: 'Failed to send email. Please check your connection.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const clearForm = () => {
    setFormData({ recipientEmail: '', subject: '', messageBody: '', title: '', message: '' });
    setResult(null);
  };

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '24px 16px' }}>
      <style>{`
        .em-page { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }

        .em-title { font-size: 22px; font-weight: 700; color: #1a1a1a; margin: 0 0 4px 0; }
        .em-subtitle { font-size: 13px; color: #888; margin: 0 0 24px 0; }

        .em-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        }
        @media (max-width: 640px) { .em-stats { grid-template-columns: repeat(2, 1fr); } }

        .em-stat {
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          padding: 14px 16px;
        }
        .em-stat-val { font-size: 22px; font-weight: 700; color: #1a1a1a; }
        .em-stat-label { font-size: 12px; color: #888; margin-top: 2px; }

        .em-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 20px;
          border-bottom: 1px solid #e8e8e8;
        }

        .em-tab {
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          color: #888;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-family: inherit;
          transition: color 0.15s, border-color 0.15s;
        }
        .em-tab:hover { color: #444; }
        .em-tab.active {
          color: #1a1a1a;
          border-bottom-color: #1a1a1a;
        }

        .em-card {
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 10px;
          overflow: hidden;
        }

        .em-card-header {
          padding: 16px 20px;
          border-bottom: 1px solid #f0f0f0;
        }
        .em-card-header h2 {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        .em-card-header p {
          font-size: 12px;
          color: #999;
          margin: 4px 0 0 0;
        }

        .em-form { padding: 20px; }

        .em-field { margin-bottom: 16px; }
        .em-field:last-of-type { margin-bottom: 0; }

        .em-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #444;
          margin-bottom: 6px;
        }

        .em-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
          background: #fafafa;
        }
        .em-input:focus {
          border-color: #999;
          background: #fff;
        }
        .em-input::placeholder { color: #bbb; }

        .em-textarea { resize: vertical; min-height: 100px; }

        .em-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 20px;
          border-top: 1px solid #f0f0f0;
          background: #fafafa;
        }

        .em-btn {
          padding: 8px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          font-family: inherit;
          cursor: pointer;
          border: none;
          transition: all 0.15s;
        }

        .em-btn-clear {
          background: #fff;
          border: 1px solid #ddd;
          color: #666;
        }
        .em-btn-clear:hover { background: #f5f5f5; }

        .em-btn-send {
          background: #1a1a1a;
          color: #fff;
        }
        .em-btn-send:hover { background: #333; }
        .em-btn-send:disabled { opacity: 0.4; cursor: not-allowed; }

        .em-result {
          margin: 0 20px 20px;
          padding: 12px 16px;
          border-radius: 6px;
          font-size: 13px;
          line-height: 1.5;
        }
        .em-result.success {
          background: #f0faf0;
          border: 1px solid #d4edda;
          color: #1e7e34;
        }
        .em-result.error {
          background: #fdf0f0;
          border: 1px solid #f5c6cb;
          color: #c0392b;
        }
        .em-result-title { font-weight: 600; margin-bottom: 2px; }
        .em-result-details {
          display: flex;
          gap: 12px;
          margin-top: 6px;
          font-size: 12px;
          color: #666;
        }

        .em-confirm-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .em-confirm-box {
          background: #fff;
          border-radius: 12px;
          padding: 28px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }
        .em-confirm-box h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 8px 0;
        }
        .em-confirm-box p {
          font-size: 13px;
          color: #666;
          margin: 0 0 20px 0;
          line-height: 1.6;
        }
        .em-confirm-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
      `}</style>

      <div className="em-page">
        <h1 className="em-title">Email Management</h1>
        <p className="em-subtitle">Send emails and notifications to your users</p>

        {emailStats && (
          <div className="em-stats">
            <div className="em-stat">
              <div className="em-stat-val">{emailStats.totalUsers}</div>
              <div className="em-stat-label">Total Users</div>
            </div>
            <div className="em-stat">
              <div className="em-stat-val">{emailStats.activeUsers}</div>
              <div className="em-stat-label">Active Users</div>
            </div>
            <div className="em-stat">
              <div className="em-stat-val">{emailStats.verifiedUsers}</div>
              <div className="em-stat-label">Verified</div>
            </div>
            <div className="em-stat">
              <div className="em-stat-val">{emailStats.unverifiedUsers}</div>
              <div className="em-stat-label">Unverified</div>
            </div>
          </div>
        )}

        <div className="em-tabs">
          <button
            className={`em-tab ${emailType === 'individual' ? 'active' : ''}`}
            onClick={() => { setEmailType('individual'); setResult(null); }}
          >
            Individual Email
          </button>
          <button
            className={`em-tab ${emailType === 'bulk' ? 'active' : ''}`}
            onClick={() => { setEmailType('bulk'); setResult(null); }}
          >
            Bulk Notification
          </button>
        </div>

        <div className="em-card">
          <div className="em-card-header">
            <h2>{emailType === 'individual' ? 'Send Individual Email' : 'Send Bulk Notification'}</h2>
            <p>{emailType === 'individual'
              ? 'Compose and send an email to a specific person'
              : 'Send a notification to all active users'}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="em-form">
              {emailType === 'individual' && (
                <>
                  <div className="em-field">
                    <label className="em-label">Recipient Email</label>
                    <input
                      type="email"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleInputChange}
                      required
                      className="em-input"
                      placeholder="e.g. john@example.com"
                    />
                  </div>
                  <div className="em-field">
                    <label className="em-label">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      maxLength={200}
                      className="em-input"
                      placeholder="Email subject"
                    />
                  </div>
                  <div className="em-field">
                    <label className="em-label">Message</label>
                    <textarea
                      name="messageBody"
                      value={formData.messageBody}
                      onChange={handleInputChange}
                      required
                      maxLength={5000}
                      rows={5}
                      className="em-input em-textarea"
                      placeholder="Write your message..."
                    />
                  </div>
                </>
              )}

              {emailType === 'bulk' && (
                <>
                  <div className="em-field">
                    <label className="em-label">Notification Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      maxLength={100}
                      className="em-input"
                      placeholder="Enter title"
                    />
                  </div>
                  <div className="em-field">
                    <label className="em-label">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={1000}
                      rows={5}
                      className="em-input em-textarea"
                      placeholder="Enter your message..."
                    />
                  </div>
                </>
              )}
            </div>

            <div className="em-actions">
              <button type="button" onClick={clearForm} className="em-btn em-btn-clear">
                Clear
              </button>
              <button type="submit" disabled={loading} className="em-btn em-btn-send">
                {loading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>

          {result && (
            <div className={`em-result ${result.success ? 'success' : 'error'}`}>
              <div className="em-result-title">
                {result.success ? 'Sent successfully' : 'Failed to send'}
              </div>
              <div>{result.message}</div>
              {result.details && (
                <div className="em-result-details">
                  {result.details.recipient && <span>To: {result.details.recipient}</span>}
                  {result.details.total !== undefined && (
                    <>
                      <span>Total: {result.details.total}</span>
                      <span>Success: {result.details.success}</span>
                      <span>Failed: {result.details.failed}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {showConfirm && (
          <div className="em-confirm-overlay" onClick={() => setShowConfirm(false)}>
            <div className="em-confirm-box" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm bulk send</h3>
              <p>
                This will send a notification to all active users
                ({emailStats?.activeUsers || 'all'} users). This cannot be undone.
              </p>
              <div className="em-confirm-actions">
                <button className="em-btn em-btn-clear" onClick={() => setShowConfirm(false)}>
                  Cancel
                </button>
                <button className="em-btn em-btn-send" onClick={handleSubmit}>
                  Send to all
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailManagement;
